import {
  dataSourceWholeItems,
  dualtimeCityList,
  sexagenaryCycleArr
} from '@/resources/data-source';
import { AQI_AQL } from '@/utils/ast/utils/data-source.utils';
import dayjs from 'dayjs';
import { round } from 'lodash-es';
import { LunarUtil, Solar } from 'lunar-javascript';
import isBetween from 'dayjs/plugin/isBetween';
import duration from 'dayjs/plugin/duration';
import { getCompassDirectionsArr } from '@/hooks/use-image-array';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { getSValue, getSSimpleValue } from './processors/s-value';

dayjs.extend(isBetween);
dayjs.extend(duration);

// 获取时间段
// 时间段的划分逻辑是：
// 当前时间段，0：凌晨，1:黎明，2:清晨，3:上午，4:中午，5:下午，6:傍晚，7:黄昏，8:晚上
// 凌晨： 24点 - 日出前1小时
// 黎明：日出前1小时 - 日出后1小时
// 清晨，上午，中午，下午，傍晚：日出后1小时-日落前1小时 时间段均分
// 黄昏：日落前1小时 - 日落后1小时
// 晚上： 24点前
export const getTimeSectionOptions = (
  configDataWhole: WatchRendererOptions['configData']
) => {
  const sunRiseHour = Number(configDataWhole?.weatherCurrentSunRiseHour ?? 0);
  const sunRiseMinute = Number(
    configDataWhole?.weatherCurrentSunRiseMinute ?? 0
  );
  const sunSetHour = Number(configDataWhole?.weatherCurrentSunSetHour ?? 0);
  const sunSetMinute = Number(configDataWhole?.weatherCurrentSunSetMinute ?? 0);

  const sunrise = dayjs().hour(sunRiseHour).minute(sunRiseMinute).second(0);
  const sunset = dayjs().hour(sunSetHour).minute(sunSetMinute).second(0);

  const s: {
    label: string;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    value: number;
  }[] = [];
  s.push({
    // 24点 - 日出前1小时
    label: '凌晨',
    start: dayjs().hour(0).minute(0).second(0),
    end: sunrise.add(-1, 'hours'),
    value: 0
  });
  s.push({
    // 日出前1小时 - 日出后1小时
    label: '黎明',
    start: sunrise.add(-1, 'h'),
    end: sunrise.add(1, 'h'),
    value: 1
  });
  const beforeSunset1h = sunset.subtract(1, 'h').diff(sunrise.add(1, 'h'), 's');
  s.push({
    label: '清晨',
    start: sunrise.add(1, 'h'),
    end: sunrise.add(1, 'h').add(beforeSunset1h / 5 - 1, 's'),
    value: 2
  });
  s.push({
    label: '上午',
    start: sunrise.add(1, 'h').add(beforeSunset1h / 5, 's'),
    end: sunrise.add(1, 'h').add((beforeSunset1h / 5) * 2 - 1, 's'),
    value: 3
  });
  s.push({
    label: '中午',
    start: sunrise.add(1, 'h').add((beforeSunset1h / 5) * 2, 's'),
    end: sunrise.add(1, 'h').add((beforeSunset1h / 5) * 3 - 1, 's'),
    value: 4
  });
  s.push({
    label: '下午',
    start: sunrise.add(1, 'h').add((beforeSunset1h / 5) * 3, 's'),
    end: sunrise.add(1, 'h').add((beforeSunset1h / 5) * 4 - 1, 's'),
    value: 5
  });
  s.push({
    label: '傍晚',
    start: sunrise.add(1, 'h').add((beforeSunset1h / 5) * 4, 's'),
    end: sunrise.add(1, 'h').add(beforeSunset1h - 1, 's'),
    value: 6
  });
  s.push({
    label: '黄昏',
    start: sunset.subtract(1, 'h'),
    end: sunset.add(1, 'h').subtract(1, 's'),
    value: 7
  });
  s.push({
    label: '晚上',
    start: sunset.add(1, 'h'),
    end: sunset.hour(23).minute(59).second(59),
    value: 8
  });

  return s;
};

const setHeartRateZone = (
  configDataWhole: WatchRendererOptions['configData']
) => {
  const res = { value: 6, label: '无效' };
  if (!configDataWhole) return res;

  const rate = Number(configDataWhole?.['healthHeartRate'] ?? 75);
  const max = Number(configDataWhole?.['healthHeartRateMax'] ?? 100);
  const _rate = rate / max;

  if (_rate < 0.5) {
    res.value = 0;
    res.label = '安静';
  } else if (_rate <= 0.6) {
    res.value = 1;
    res.label = '热身';
  } else if (_rate <= 0.7) {
    res.value = 2;
    res.label = '燃脂';
  } else if (_rate <= 0.8) {
    res.value = 3;
    res.label = '有氧';
  } else if (_rate <= 0.9) {
    res.value = 4;
    res.label = '无氧';
  } else {
    res.value = 5;
    res.label = '极限';
  }
  return res;
};

const getTimeSectionIndex = (
  configDataWhole: WatchRendererOptions['configData']
) => {
  const timeHour = Number(configDataWhole?.['timeHour24H'] ?? 0);
  const timeMinute = Number(configDataWhole?.['timeMinute'] ?? 0);
  const timeSecond = Number(configDataWhole?.['timeSecond'] ?? 0);
  // 加一秒防止重复
  const time = dayjs()
    .hour(timeHour)
    .minute(timeMinute)
    .second(timeSecond + 1);
  const sections = getTimeSectionOptions(configDataWhole);

  const timeSectionIndex =
    sections.find((item) => {
      return time.isAfter(item.start) && time.isBefore(item.end);
    })?.value ?? 0;

  return timeSectionIndex;
};

// 根据指南针角度，获取当前指南针方向
export const getCompassDirection = (
  configDataWhole: WatchRendererOptions['configData']
) => {
  const compass = Number(configDataWhole?.['systemSensorCompass'] ?? 0);
  return (
    [...getCompassDirectionsArr(true)].reverse().find((item) => {
      return Number(item.value) <= compass;
    })?.label ?? '北'
  );
};
const moonPhaseRanges: Record<string, number> = {
  '0-1': 0, //'新月'
  '2-6': 1, //'蛾眉月'
  '7-8': 2, //'上弦月'
  '9-14': 3, //'盈亏月'
  '15-16': 4, //'满月'
  '17-20': 5, //'亏凸月'
  '21-22': 6, //'下弦月'
  '23-30': 7 //'残月'
};

const dataMoonSources: string[] = [
  'dateMoon',
  'dateMoonTomorrow',
  'dateMoonAfterTomorrow',
  'dateMoonThreeTomorrow',
  'dateMoonFourDaysAgo',
  'dateMoonFiveDaysAgo',
  'dateMoonSixDaysAgo'
];

const getDateMoon = (configDataWhole: WatchRendererOptions['configData']) => {
  const calculateMoonPhase = (lunarDay: number) => {
    for (const range in moonPhaseRanges) {
      const [start, end] = range.split('-').map(Number);
      if (lunarDay >= start && lunarDay <= end) {
        return moonPhaseRanges[range];
      }
    }
  };
  if (!configDataWhole) return;
  const year = Number(configDataWhole['dateYear']);
  const month = Number(configDataWhole['dateMonth']);
  const day = Number(configDataWhole['dateDay']);
  const oDay = dayjs(`${year}-${month}-${day}`);
  dataMoonSources.forEach((sourceName, index) => {
    if (index === 0) {
      const lunar = Solar.fromYmd(year, month, day).getLunar();
      const currentLunar = lunar.getDay();
      const currentMoon = calculateMoonPhase(currentLunar);
      if (currentMoon != undefined) configDataWhole[sourceName] = currentMoon;
    } else {
      // 计算未来日期的阴历
      const _futureDay = Number(oDay.add(index, 'days').format('D'));
      const _futureMonth = Number(oDay.add(index, 'days').format('M'));
      const _futureYear = Number(oDay.add(index, 'days').format('YYYY'));
      const lunar = Solar.fromYmd(
        _futureYear,
        _futureMonth,
        _futureDay
      ).getLunar();
      const futureLunar = lunar.getDay();
      const futureMoon = calculateMoonPhase(futureLunar);
      if (futureMoon != undefined) configDataWhole[sourceName] = futureMoon;
    }
  });
};

export const getRuntimeConfigData = (
  configData: WatchRendererOptions['configData'],
  // 不可关联的数据源
  unAssociate?: WatchRendererOptions['configData']
) => {
  // 添加配置值
  const configDataWhole: Record<string, string | number> = {};
  const watchfaceStore = useWatchfaceStore();
  // 将所有数据源都添加到configDataWhole
  const dataItems = dataSourceWholeItems;
  dataItems.forEach((item) => {
    const key = item.key;

    item.defaultValue = item.defaultValueFn
      ? item.defaultValueFn(watchfaceStore.deviceType)
      : item.defaultValue;

    // 优先使用配置数据
    const val =
      unAssociate?.[key] ??
      configData?.[key] ??
      item.defaultValue ??
      item.value?.() ??
      0;
    configDataWhole[key] = val;
  });

  // 处理昨天和明天日期
  // 日期跟月份和年联动，需要逐个计算
  const year = Number(configDataWhole['dateYear']);
  const month = Number(configDataWhole['dateMonth']);
  const _oDay = dayjs(`${year}-${month}-1`);
  const day = Math.min(Number(configDataWhole['dateDay']), _oDay.daysInMonth());
  const oDay = dayjs(`${year}-${month}-${day}`);
  const val = Math.floor(Number(day)).toString().padStart(2, '0');
  configDataWhole['dateDayHigh'] = Number(val[0]);
  configDataWhole['dateDayLow'] = Number(val[1]);

  // 上午或下午
  const hour = Number(configDataWhole['timeHour']);
  configDataWhole['miscIsPM'] = hour >= 12 ? 1 : 0;

  // 12/24小时时间
  configDataWhole['timeHour12H'] = hour % 12 || 12;
  configDataWhole['timeHour24H'] = hour;

  // 双时区 上午或下午
  const dualtimeHour = Number(configDataWhole['dualtimeHour']);
  configDataWhole['dualtimeIsPM'] = dualtimeHour >= 12 ? 1 : 0;

  // 根据12或24小时制，处理时间
  const is12Hour = configDataWhole['miscIs24H'] == 0;
  if (is12Hour) {
    [
      'timeHour',
      'weatherCurrentSunRise',
      'weatherCurrentSunSet',
      'appAlarmHour',
      // 双时区
      'dualtimeHour'
    ].forEach((key) => {
      const val = Math.floor(Number(configDataWhole[key]));
      const _val = val == 0 ? val + 12 : val > 12 ? val - 12 : val;
      configDataWhole[key] = _val;
    });
  }
  // 时间高位和低位类型(仅限于2位数)
  [
    'timeHour',
    'timeHour12H',
    'timeHour24H',
    'timeMinute',
    'timeSecond',
    'dateMonth',
    // 双时区
    'dualtimeHour',
    'dualtimeMinute',
    'dualtimeSecond'
  ].forEach((key) => {
    const val = Math.floor(Number(configDataWhole[key]))
      .toString()
      .padStart(2, '0');
    configDataWhole[key + 'High'] = Number(val[0]);
    configDataWhole[key + 'Low'] = Number(val[1]);
  });
  // 处理年
  const dateYear = Math.floor(Number(configDataWhole['dateYear']))
    .toString()
    .padStart(4, '0');
  configDataWhole['dateYearDigit1'] = Number(dateYear[3] ?? 0);
  configDataWhole['dateYearDigit2'] = Number(dateYear[2] ?? 0);
  configDataWhole['dateYearDigit3'] = Number(dateYear[1] ?? 0);
  configDataWhole['dateYearDigit4'] = Number(dateYear[0] ?? 0);

  // 处理星期字符串
  configDataWhole['dateWeek'] = unAssociate?.['dateWeek'] ?? oDay.day();
  const dateWeek = Number(configDataWhole['dateWeek']);
  [
    'dateWeekStringShortCN',
    'dateWeekStringFullCN',
    'dateWeekStringFullPascalEN',
    'dateWeekStringFullUpperEN',
    'dateWeekStringFullLowerEN',
    'dateWeekStringShortPascalEN',
    'dateWeekStringShortUpperEN',
    'dateWeekStringShortLowerEN'
  ].forEach((key) => {
    configDataWhole[key] =
      dataItems.find((item) => item.key == key)?.labelRange?.[dateWeek] ?? '';
  });
  // 处理月份字符串
  const dateMonth = Number(configDataWhole['dateMonth']);
  [
    'dateMonthStringShortCN',
    'dateMonthStringFullPascalEN',
    'dateMonthStringFullUpperEN',
    'dateMonthStringFullLowerEN',
    'dateMonthStringShortPascalEN',
    'dateMonthStringShortUpperEN',
    'dateMonthStringShortLowerEN'
  ].forEach((key) => {
    configDataWhole[key] =
      dataItems.find((item) => item.key == key)?.labelRange?.[dateMonth - 1] ??
      '';
  });
  // 步数
  const healthStepCount = Math.floor(
    Number(configDataWhole['healthStepCount'] ?? 0)
  )
    .toString()
    .padStart(5, '0');
  configDataWhole['healthStepCountDigit1'] = Number(healthStepCount[4] ?? 0);
  configDataWhole['healthStepCountDigit2'] = Number(healthStepCount[3] ?? 0);
  configDataWhole['healthStepCountDigit3'] = Number(healthStepCount[2] ?? 0);
  configDataWhole['healthStepCountDigit4'] = Number(healthStepCount[1] ?? 0);
  configDataWhole['healthStepCountDigit5'] = Number(healthStepCount[0] ?? 0);

  // 卡路里
  const healthCalorieValue = Math.floor(
    Number(configDataWhole['healthCalorieValue'] ?? 0)
  )
    .toString()
    .padStart(5, '0');
  configDataWhole['healthCalorieValueDigit1'] = Number(
    healthCalorieValue[4] ?? 0
  );
  configDataWhole['healthCalorieValueDigit2'] = Number(
    healthCalorieValue[3] ?? 0
  );
  configDataWhole['healthCalorieValueDigit3'] = Number(
    healthCalorieValue[2] ?? 0
  );
  configDataWhole['healthCalorieValueDigit4'] = Number(
    healthCalorieValue[1] ?? 0
  );
  configDataWhole['healthCalorieValueDigit5'] = Number(
    healthCalorieValue[0] ?? 0
  );

  // 中高强度锻炼时长
  const healthExerciseDuration = Math.floor(
    Number(configDataWhole['healthExerciseDuration'] ?? 0)
  )
    .toString()
    .padStart(3, '0');
  configDataWhole['healthExerciseDurationDigit1'] = Number(
    healthExerciseDuration[2] ?? 0
  );
  configDataWhole['healthExerciseDurationDigit2'] = Number(
    healthExerciseDuration[1] ?? 0
  );
  configDataWhole['healthExerciseDurationDigit3'] = Number(
    healthExerciseDuration[0] ?? 0
  );

  // 站立次数
  const healthStandCount = Math.floor(
    Number(configDataWhole['healthStandCount'] ?? 0)
  )
    .toString()
    .padStart(2, '0');
  configDataWhole['healthStandCountDigit1'] = Number(healthStandCount[1] ?? 0);
  configDataWhole['healthStandCountDigit2'] = Number(healthStandCount[0] ?? 0);

  configDataWhole['dateDay'] = Number(oDay.format('D'));
  // 昨天
  configDataWhole['miscdateYestarday'] = Number(
    oDay.subtract(1, 'days').format('D')
  );

  // 明天
  configDataWhole['miscdateTomorrow'] = Number(oDay.add(1, 'days').format('D'));
  // 前天日期
  configDataWhole['miscdateDayBeforeYestarday'] = Number(
    oDay.subtract(2, 'days').format('D')
  );
  // 大前天日期
  configDataWhole['miscdateDayThreeDaysAgo'] = Number(
    oDay.subtract(3, 'days').format('D')
  );
  // 后天日期
  configDataWhole['miscdateDayAfterTomorrow'] = Number(
    oDay.add(2, 'days').format('D')
  );
  configDataWhole['miscdateDayThreeDaysFromNow'] = Number(
    oDay.add(3, 'days').format('D')
  );

  // 昨天星期
  const _weekDay = oDay.day(dateWeek);
  configDataWhole['miscdateYestardayWeak'] = Number(
    _weekDay.subtract(1, 'days').day()
  );
  // 明天星期
  configDataWhole['miscdateTomorrowWeak'] = Number(
    _weekDay.add(1, 'days').day()
  );

  // 处理天干地支
  const lunar = Solar.fromYmd(year, month, day).getLunar();
  const ganzhi = lunar.getYearInGanZhi();
  const currSexagenaryCycleIndex = sexagenaryCycleArr.findIndex(
    (item) => item == ganzhi
  );
  configDataWhole['dateSexagenaryCycle'] =
    unAssociate?.['dateSexagenaryCycle'] ?? currSexagenaryCycleIndex;
  // 天干地支字符串
  configDataWhole['dateLunarStringYear'] =
    sexagenaryCycleArr[Number(configDataWhole['dateSexagenaryCycle'])];

  configDataWhole['dateLunarStringMonth'] = lunar.getMonthInChinese() + '月';
  configDataWhole['dateLunarStringDay'] = lunar.getDayInChinese();
  configDataWhole['dateLunarYear'] = lunar.getYear();
  // console.log(
  //   'dateLunarYear',
  //   year,
  //   month,
  //   day,
  //   configDataWhole['dateLunarYear']
  // );
  configDataWhole['dateLunarDay'] = lunar.getDay();
  configDataWhole['dateLunarMonth'] = lunar.getMonth();
  // 处理闰月
  if (configDataWhole['dateLunarMonth'] < 0) {
    configDataWhole['dateLunarMonth'] = lunar.getMonth() * -1;
  }

  // 时辰
  configDataWhole['timeChineseTimeString'] =
    LunarUtil.convertTime(
      configDataWhole['timeHour24HHigh'] +
        '' +
        configDataWhole['timeHour24HLow'] +
        ':' +
        configDataWhole['timeMinuteHigh'] +
        '' +
        configDataWhole['timeMinuteLow']
    ) + '时';

  // 血压不同的单位
  // 默认为mmHg
  // [
  //   'healthBloodDiastolicPressureMmhg',
  //   'healthBloodSystolicPressureMmhg'
  // ].forEach((key) => {
  //   const val = Number(configDataWhole[key] ?? 0);
  //   const key2 = key.replace('Mmhg', 'Kpa');
  //   // mmHg换算成Kpa
  //   configDataWhole[key2] = mmHgToKpa(val);
  // });

  // // 温度不同的单位
  // // 默认为摄氏度
  // [
  //   'weatherCurrentTemperature',
  //   'weatherTodayTemperatureMin',
  //   'weatherTodayTemperatureMax',
  //   'weatherTomorrowTemperatureMin',
  //   'weatherTomorrowTemperatureMax'
  // ].forEach((key) => {
  //   const val = Number(configDataWhole[key] ?? 0);
  //   const key2 = key + 'Fahrenheit';

  //   // 摄氏度换算成华氏度
  //   configDataWhole[key2] = celsiusToFahrenheit(val);
  // });

  // 双时区-城市列表字符串
  configDataWhole['dualtimeStringCity'] =
    dualtimeCityList[Number(configDataWhole['dualtimeCity'] ?? 0)].brief;

  // 处理禁飞时间和水面间隔时间-单位分钟
  ['appSportNoFlyTime', 'appSportSitTime'].forEach((key) => {
    const val = Number(configDataWhole[key]);
    // 先处理单位
    let unit = 1;
    let _unitLabel = '分钟';
    if (val < 60) {
      unit = 1;
    } else if (val < 60 * 24) {
      unit = 2;
      _unitLabel = '小时';
    } else if (val < 60 * 24 * 3) {
      unit = 3;
      _unitLabel = '天';
    } else {
      unit = 0;
      _unitLabel = '-';
    }

    const unitKey = key + 'Unit';
    configDataWhole[unitKey] = unit;
    configDataWhole[unitKey + 'Label'] = _unitLabel;

    // 在根据单位处理时间
    let val2 = val;
    if (unit == 1) {
      // 分钟，精确到个位
      val2 = Math.floor(val);
    } else if (unit == 2) {
      // 小时，精确到2位
      val2 = round(val / 60, 2);
    } else if (unit == 3) {
      val2 = round(val / 60 / 24, 2);
    } else {
      val2 = Number.NaN;
    }
    configDataWhole[key] = val2;
  });

  // 处理心率区间
  const { value: rateZone, label: rateZoneLabel } =
    setHeartRateZone(configDataWhole);
  if (unAssociate?.['healthHeartRateZone'] == undefined) {
    configDataWhole['healthHeartRateZone'] = rateZone;
    configDataWhole['healthHeartRateZoneString'] = rateZoneLabel;
  }

  // 天气质量等级 weatherCurrentAirQualityLevel（老设备通过 AQI 计算）
  // weatherAirQualityLevel（新设备固件直接下发等级值）
  const useDirectAirQualityLevel = [
    'P62',
    'Q61',
    'Q63',
    'Q66',
    'O61',
    'P67'
  ].includes(watchfaceStore.deviceType);
  if (useDirectAirQualityLevel) {
    const level = Number(
      configDataWhole['weatherAirQualityLevel'] ?? 0
    );
    const levelItem = AQI_AQL.find((item) => item.level === level);
    configDataWhole['weatherAirQualityLevelLabel'] =
      levelItem?.label ?? '';
  }
  // 老设备通过 AQI 计算等级
  {
    const aqi = Number(configDataWhole['weatherCurrentAirQualityIndex'] ?? 0);
    const levelItem = AQI_AQL.findLast((item) => {
      return aqi >= item.value;
    });
    configDataWhole['weatherCurrentAirQualityLevel'] = levelItem?.level ?? 0;
    configDataWhole['weatherCurrentAirQualityLevelLabel'] =
      levelItem?.label ?? '';
  }

  // 处理时间段
  configDataWhole['miscTimeSection'] =
    unAssociate?.['miscTimeSection'] ?? getTimeSectionIndex(configDataWhole);

  // 根据指南针角度确定指南针方向
  configDataWhole['systemSensorCompassDirection'] =
    getCompassDirection(configDataWhole);

  // console.log(configDataWhole);

  getDateMoon(configDataWhole);
  getSValue(configDataWhole);
  getSSimpleValue(configDataWhole);
  return configDataWhole;
};
